import React from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { defaultTemplate } from 'core/hoc';
import { BaasicButton, BaasicHeader } from 'core/components';
import BillingPaged from './BillingPaged';

const statusIcons = {
	Created: 'type--color--success fa fa-check-circle',
	AttachmentFailed: 'type--color--warning-light fa fa-exclamation-circle',
	NoCustomer: 'type--color--warning-light fa fa-exclamation-circle',
	NothingToBill: 'type--color--warning-light fa fa-minus-circle',
	Failed: 'type--color--error fa fa-times-circle',
};

// One row per matched seller: what its QuickBooks invoice would be, with a checkbox to send it.
const SendToQuickBooks = observer(({ t, usd, viewStore }) => {
	const { sellerSummaries, selectedSummaries, selectedSellerIds, isEverySellerSelected, isRunning } = viewStore;
	return (
		<div className="box--primary box--sml spc--bottom--sml">
			<div className="flex--primary spc--bottom--sml">
				<strong className="spc--right--med">{t('BILLING.SENDTITLE')}</strong>
				<BaasicButton
					className="btn btn--sml btn--primary"
					type="button"
					label={t('BILLING.SEND', {
						count: selectedSummaries.length,
						amount: usd(_.sumBy(selectedSummaries, 'total')),
					})}
					disabled={isRunning || selectedSummaries.length === 0}
					onClick={viewStore.confirmSendToQuickBooks}
				/>
			</div>
			{sellerSummaries.length === 0 ? (
				<p className="type--sml">{t('BILLING.NOTHINGTOSEND')}</p>
			) : (
				<div className="billing__scroll">
					<table className="table table--secondary">
						<thead>
							<tr>
								<th className="border">
									<input
										type="checkbox"
										checked={isEverySellerSelected}
										onChange={viewStore.toggleEverySeller}
										disabled={isRunning}
									/>
								</th>
								<th className="border">{t('BILLING.SELLER')}</th>
								<th className="border type--right">{t('BILLING.PACKAGES')}</th>
								<th className="border type--right">{t('BILLING.NETCHARGE')}</th>
								<th className="border type--right">{t('BILLING.UPCHARGE')}</th>
								<th className="border type--right">{t('BILLING.INVOICETOTAL')}</th>
								<th className="border" />
							</tr>
						</thead>
						<tbody>
							{_.map(sellerSummaries, seller => (
								<tr key={seller.sellerId}>
									<td>
										<input
											type="checkbox"
											checked={selectedSellerIds.has(seller.sellerId)}
											onChange={() => viewStore.toggleSeller(seller.sellerId)}
											disabled={isRunning}
										/>
									</td>
									<td>{seller.sellerName}</td>
									<td className="type--right">{seller.packageCount}</td>
									<td className="type--right">{usd(seller.netCharge)}</td>
									<td className="type--right">{usd(seller.upcharge)}</td>
									<td className="type--right">
										<strong>{usd(seller.total)}</strong>
									</td>
									<td>
										{seller.packagesWithoutUpchargeCount > 0 && (
											<span className="type--color--warning-light">
												<i className="fa fa-exclamation-circle spc--right--nano" />
												{t('BILLING.PACKAGESATCOST', { count: seller.packagesWithoutUpchargeCount })}
											</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
});

// The sellers the last send couldn't invoice, with why; the ones it did invoice are in the sent invoices below.
const NotInvoiced = ({ t, usd, sellers }) => (
	<div className="box--primary box--sml spc--bottom--sml">
		<strong className="display--b spc--bottom--sml">{t('BILLING.NOTINVOICED')}</strong>
		<div className="billing__scroll">
			<table className="table table--secondary">
				<thead>
					<tr>
						<th className="border">{t('BILLING.SELLER')}</th>
						<th className="border type--right">{t('BILLING.CHARGES')}</th>
						<th className="border type--right">{t('BILLING.AMOUNT')}</th>
						<th className="border">{t('BILLING.STATUS')}</th>
						<th className="border">{t('BILLING.MESSAGE')}</th>
					</tr>
				</thead>
				<tbody>
					{_.map(sellers, seller => (
						<tr key={seller.sellerId}>
							<td>{seller.sellerName}</td>
							<td className="type--right">{seller.chargeCount}</td>
							<td className="type--right">{usd(seller.amount)}</td>
							<td>
								<i className={`${statusIcons[seller.status] || ''} spc--right--nano`} />
								{t(`BILLING.STATUSES.${seller.status}`)}
							</td>
							<td>{seller.message}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	</div>
);

// A labelled filter box; the labels stay visible, unlike placeholders, which looked like empty lines.
const Filter = ({ t, viewStore, name, type, label }) => (
	<label className="billing__filter">
		<span className="type--sml">{t(label)}</span>
		<input
			className="input input--sml input--text"
			type={type}
			value={viewStore.sentInvoiceFilters[name]}
			onChange={e => viewStore.setSentInvoiceFilter(name, e.target.value)}
		/>
	</label>
);

// Every invoice already created in QuickBooks, filterable by seller, exact amount and date sent.
const SentInvoices = observer(({ t, usd, viewStore }) => {
	const { sentInvoices, filteredSentInvoices, sentInvoicesPageRows: page } = viewStore;
	return (
		<div className="box--primary box--sml spc--bottom--sml">
			<strong className="display--b spc--bottom--sml">
				{t('BILLING.SENTINVOICES', {
					count: filteredSentInvoices.length,
					amount: usd(_.sumBy(filteredSentInvoices, 'amount')),
				})}
			</strong>
			<div className="billing__filters spc--bottom--sml">
				<Filter t={t} viewStore={viewStore} name="seller" type="text" label="BILLING.FILTERSELLER" />
				{/* Text, not number: a number box drops "$1,275.12" as invalid before the filter sees it. */}
				<Filter t={t} viewStore={viewStore} name="amount" type="text" label="BILLING.FILTERAMOUNT" />
				<Filter t={t} viewStore={viewStore} name="fromDate" type="date" label="BILLING.SENTFROM" />
				<Filter t={t} viewStore={viewStore} name="toDate" type="date" label="BILLING.SENTTO" />
				<BaasicButton
					className="btn btn--sml btn--ghost"
					type="button"
					label="BILLING.CLEARFILTERS"
					onClick={viewStore.clearSentInvoiceFilters}
				/>
			</div>
			{_.isEmpty(sentInvoices) ? (
				<p className="type--sml">{t('BILLING.NOSENTINVOICES')}</p>
			) : (
				<BillingPaged t={t} page={page} onPage={viewStore.setSentInvoicesPage}>
					<div className="billing__scroll">
						<table className="table table--secondary">
							<thead>
								<tr>
									<th className="border">{t('BILLING.DATESENT')}</th>
									<th className="border">{t('BILLING.SELLER')}</th>
									<th className="border">{t('BILLING.INVOICENUMBER')}</th>
									<th className="border type--right">{t('BILLING.INVOICEAMOUNT')}</th>
								</tr>
							</thead>
							<tbody>
								{_.map(page.rows, invoice => (
									<tr key={invoice.quickBooksInvoiceId}>
										<td>{invoice.sentAt.format('L LT')}</td>
										<td>{invoice.sellerName || '-'}</td>
										<td>{invoice.quickBooksDocNumber ? `#${invoice.quickBooksDocNumber}` : '-'}</td>
										<td className="type--right">{usd(invoice.amount)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</BillingPaged>
			)}
		</div>
	);
});

const BillingQuickBooksTemplate = ({ viewStore, t }) => {
	const { rootStore, isRunning, currentStep, notInvoiced, refresh, matches, sentInvoices } = viewStore;
	const usd = value => (value == null ? '-' : rootStore.usdFormatter.format(value));

	return (
		<React.Fragment>
			<BaasicHeader title="MAINMENU.BILLING.EXPORTTOQUICKBOOKS"></BaasicHeader>
			<div className="container">
				<div className="box--primary box--sml spc--bottom--sml flex--primary">
					<p className="type--sml spc--right--med">{t('BILLING.QUICKBOOKSDESCRIPTION')}</p>
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative"
						type="button"
						icon="fa fa-redo spc--right--sml"
						label="BILLING.RELOAD"
						disabled={isRunning}
						onClick={refresh}
					/>
				</div>

				{isRunning && (
					<div className="box--primary box--sml spc--bottom--sml flex--primary">
						<div className="spinner-inline-small"></div>
						<strong className="spc--left--sml">{t(currentStep)}</strong>
					</div>
				)}

				{notInvoiced.length > 0 && <NotInvoiced t={t} usd={usd} sellers={notInvoiced} />}

				{matches != null && <SendToQuickBooks t={t} usd={usd} viewStore={viewStore} />}

				{sentInvoices != null && <SentInvoices t={t} usd={usd} viewStore={viewStore} />}
			</div>
		</React.Fragment>
	);
};

export default defaultTemplate(BillingQuickBooksTemplate);
